import { useState } from 'react';

import { copy } from '../../core/copy';
import { formatMinutes } from '../../core/lang';
import { relativeDays } from '../../core/time';
import { isNodeUnlocked, nodeStatusOf, revisionMinutes } from '../../core/rules';
import { checkLink } from '../../core/links';
import { Btn, Modal, NodeGlyph } from '../components';
import { PrimljeniKomentari } from './Komentari';
import { sfx } from '../audio';
import { act, contentOf, isReadOnly, openNode, playerOf, useStore } from '../store';

/**
 * The node card, and with it the only mechanic in this product that really matters.
 *
 * Four steps, always visible in full from the first moment — so the student can see, before they
 * start, that handing in the first attempt is not the end of the task. Step four is the one the
 * research says people skip and the one where the learning actually happens, so it is drawn
 * differently and it is the only step that says out loud why it is worth more.
 */
export function NodeCard({ nodeId }: { nodeId: string }) {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const readOnly = useStore(isReadOnly);

  const island = content.islands.find((i) => i.nodes.some((n) => n.id === nodeId));
  const node = island?.nodes.find((n) => n.id === nodeId);
  if (!island || !node) return null;

  const progress = player.progress[node.id];
  const status = nodeStatusOf(node, progress);
  const unlocked = isNodeUnlocked(content, player, island, node);
  const close = () => openNode(null);

  return (
    <Modal title={island.name} onClose={close}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <NodeGlyph kind={node.kind} done={status === 'zavrsen' || status === 'popravljen'} scale={2} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="title" style={{ fontSize: 17 }}>
            {node.title}
          </h2>
          <p className="tiny muted" style={{ marginTop: 2 }}>
            {copy.node.kinds[node.kind]} · ~
            {formatMinutes(status === 'feedback' ? revisionMinutes(node) : node.estMin)}
            {node.optional ? ` · ${copy.node.optional}` : ''}
          </p>
        </div>
      </div>

      {node.task ? <p style={{ fontSize: 14, marginBottom: 14 }}>{node.task}</p> : null}

      {node.lessonUrls.length > 0 ? (
        <div className="banner" style={{ borderColor: 'var(--paper-edge)', background: 'var(--paper-shade)' }}>
          <b className="tiny" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            {copy.node.lessonsIn}
          </b>
          <p className="tiny" style={{ marginTop: 6 }}>
            <a href={node.skoolUrl ?? '#'} target="_blank" rel="noreferrer noopener">
              {node.lessonUrls.length > 1
                ? `${copy.node.lessonCount(node.lessonUrls.length)} ↗`
                : 'Otvori lekciju ↗'}
            </a>
          </p>
          {node.lessonUrls.length > 1 ? (
            <p className="tiny muted" style={{ marginTop: 4 }}>
              Redom, u kursu. Kad ih pređeš, vrati se i označi.
            </p>
          ) : null}
        </div>
      ) : null}

      {node.note ? (
        <div className="timeline-note">
          {node.note.tekst}
          {node.note.ko ? <div className="who">— {node.note.ko}</div> : null}
        </div>
      ) : null}

      {!unlocked ? (
        <div className="banner bad">
          <b>{copy.node.locked}</b>
          <p className="tiny" style={{ marginTop: 4 }}>
            {copy.node.lockedHint}
          </p>
        </div>
      ) : node.submission === 'nista' ? (
        <LessonSteps node={node} status={status} readOnly={readOnly} onDone={close} />
      ) : (
        <SubmissionSteps node={node} status={status} progress={progress} readOnly={readOnly} />
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ lessons */

function LessonSteps({
  node,
  status,
  readOnly,
  onDone,
}: {
  node: { id: string; title: string };
  status: string;
  readOnly: boolean;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const done = status === 'zavrsen';

  return (
    <>
      <div className={`step ${done ? 'done' : 'active'}`}>
        <span className="num">01</span>
        <div className="body">
          <b>{copy.sada.lessonsLead}</b>
        </div>
      </div>
      {error ? <div className="banner bad">{error}</div> : null}
      <div className="btn-row" style={{ marginTop: 14 }}>
        <Btn
          variant="primary"
          disabled={done || readOnly}
          onClick={() => {
            void act(node.id, 'done').then((result) => {
              if (result.ok) onDone();
              else setError(result.message);
            });
          }}
        >
          {done ? '✓' : copy.sada.doneButton}
        </Btn>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ submissions */

function SubmissionSteps({
  node,
  status,
  progress,
  readOnly,
}: {
  node: { id: string; needsRevision: boolean };
  status: string;
  progress: { v1Url?: string | null; v1At?: number | null; v2Url?: string | null } | undefined;
  readOnly: boolean;
}) {
  const [firstLink, setFirstLink] = useState('');
  const [secondLink, setSecondLink] = useState('');
  // A soft warning, never a block. Plenty of people will paste a Drive or YouTube link and it is
  // better to nudge them than to reject work they actually did.
  const offCommunity = (value: string) => {
    const check = checkLink(value);
    return value.trim().length > 6 && check.ok && !check.community;
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const posted = Boolean(progress?.v1At);
  const gotFeedback = status === 'feedback' || status === 'popravljen';
  const revised = status === 'popravljen';

  const submit = async (action: 'post' | 'revise' | 'feedback', url?: string) => {
    setBusy(true);
    setError(null);
    const result = await act(node.id, action, url);
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message);
    }
  };

  return (
    <>
      <div className={`step ${posted ? 'done' : 'active'}`}>
        <span className="num">01</span>
        <div className="body">
          <b>{copy.node.steps.do}</b>
        </div>
      </div>

      <div className={`step ${posted ? 'done' : 'active'}`}>
        <span className="num">02</span>
        <div className="body">
          <b>{copy.node.steps.post}</b>
          {posted ? (
            <p>
              <a href={progress?.v1Url ?? '#'} target="_blank" rel="noreferrer noopener">
                {copy.journal.work}
              </a>
              {progress?.v1At ? ` · ${relativeDays(progress.v1At, Date.now())}` : ''}
            </p>
          ) : (
            <>
              <p>{copy.node.steps.postHint}</p>
              <input
                className="field"
                style={{ marginTop: 8 }}
                type="url"
                inputMode="url"
                autoCapitalize="off"
                spellCheck={false}
                placeholder={copy.node.linkPlaceholder}
                value={firstLink}
                onChange={(event) => setFirstLink(event.target.value)}
                disabled={readOnly || busy}
              />
              {offCommunity(firstLink) ? (
                <p className="tiny" style={{ marginTop: 6, color: 'var(--gold-dark)' }}>
                  {copy.submit.notSkool}
                </p>
              ) : null}
              <div className="btn-row" style={{ marginTop: 10 }}>
                <Btn
                  variant="primary"
                  size="small"
                  disabled={!firstLink.trim() || busy || readOnly}
                  onClick={() => void submit('post', firstLink.trim())}
                >
                  {copy.sada.postedButton}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>

      {node.needsRevision ? (
        <>
          <div className={`step ${gotFeedback ? 'done' : posted ? 'active' : ''}`.trim()}>
            <span className="num">03</span>
            <div className="body">
              <b>{copy.node.steps.feedback}</b>
              <p>{gotFeedback ? copy.node.steps.feedbackHint : copy.node.gotFeedbackHint}</p>
              {/* Who commented, and a way straight to what they wrote — on the step that is about
                  waiting for exactly that. Renders nothing when the feedback was self-reported. */}
              <PrimljeniKomentari nodeId={node.id} />
              {posted && !gotFeedback ? (
                <div className="btn-row" style={{ marginTop: 10 }}>
                  <Btn
                    variant="ghost"
                    size="small"
                    disabled={busy || readOnly}
                    onClick={() => void submit('feedback')}
                  >
                    {copy.node.gotFeedback}
                  </Btn>
                </div>
              ) : null}
            </div>
          </div>

          <div className={`step ${revised ? 'done' : gotFeedback ? 'gold' : ''}`.trim()}>
            <span className="num">04</span>
            <div className="body">
              <b style={{ color: revised ? undefined : 'var(--gold-dark)' }}>
                {copy.node.steps.revise}
              </b>
              <p>{copy.node.steps.reviseHint}</p>
              {revised ? (
                <p>
                  <a href={progress?.v2Url ?? '#'} target="_blank" rel="noreferrer noopener">
                    {copy.journal.work}
                  </a>
                </p>
              ) : gotFeedback ? (
                <>
                  <input
                    className="field"
                    style={{ marginTop: 8 }}
                    type="url"
                    inputMode="url"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder={copy.node.revisionLinkPlaceholder}
                    value={secondLink}
                    onChange={(event) => setSecondLink(event.target.value)}
                    disabled={readOnly || busy}
                  />
                  {offCommunity(secondLink) ? (
                    <p className="tiny" style={{ marginTop: 6, color: 'var(--gold-dark)' }}>
                      {copy.submit.notSkool}
                    </p>
                  ) : null}
                  <div className="btn-row" style={{ marginTop: 10 }}>
                    <Btn
                      variant="gold"
                      size="small"
                      disabled={!secondLink.trim() || busy || readOnly}
                      onClick={() => void submit('revise', secondLink.trim())}
                    >
                      {copy.sada.reviseButton}
                    </Btn>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {error ? (
        <div className="banner bad" style={{ marginTop: 12, marginBottom: 0 }}>
          {error}
        </div>
      ) : null}
      {readOnly ? (
        <div className="banner" style={{ marginTop: 12, marginBottom: 0 }}>
          {copy.login.readOnly}
        </div>
      ) : null}
    </>
  );
}
